import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, User, ArrowLeft, UserPlus, Settings, Shield, Info, UserCog, Calendar, AlertTriangle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function UsersPage() {
  const { token, user: currentUser, settings } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [adminInstructionsDialogOpen, setAdminInstructionsDialogOpen] = useState(false);
  const [adminInstructionsContent, setAdminInstructionsContent] = useState({ hasAdmin: false, message: "" });
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    cognome: "",
    nome: "",
    email: "",
    ruolo: ""
  });
  const [permissionsForm, setPermissionsForm] = useState({
    gestione_info_azienda: false,
    gestione_giorni_lavorativi: false,
    gestione_limiti_richieste: false,
    gestione_eventi: false,
    gestione_ferie_permessi: false
  });
  const [balanceForm, setBalanceForm] = useState({
    ferie_totali_annue: 26,
    ferie_riporto: 0,
    ferie_godute: 0,
    permessi_totali_annui: 32,
    permessi_riporto: 0,
    permessi_goduti: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  // Check if this is the first time titolare visits the users page
  useEffect(() => {
    if (!loading && currentUser?.ruolo === "titolare" && users.length > 0) {
      const hasSeenInstructions = localStorage.getItem(`admin_instructions_seen_${currentUser.id}`);
      if (!hasSeenInstructions) {
        const hasAmministrativo = users.some(u => u.ruolo === "amministrativo");
        if (hasAmministrativo) {
          setAdminInstructionsContent({
            hasAdmin: true,
            message: "Gestisci cosa può inserire/variare un Amministrativo"
          });
        } else {
          setAdminInstructionsContent({
            hasAdmin: false,
            message: "Ricordati quando si registrerà un Amministrativo di dare il permesso per cosa gestire inserire/variare"
          });
        }
        setAdminInstructionsDialogOpen(true);
      }
    }
  }, [loading, currentUser, users]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento degli utenti");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAdminInstructions = () => {
    if (currentUser?.id) {
      localStorage.setItem(`admin_instructions_seen_${currentUser.id}`, "true");
    }
    setAdminInstructionsDialogOpen(false);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({
      cognome: user.cognome,
      nome: user.nome,
      email: user.email,
      ruolo: user.ruolo
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handlePermissionsClick = (user) => {
    setSelectedUser(user);
    setPermissionsForm({
      gestione_info_azienda: user.permessi_admin?.gestione_info_azienda || false,
      gestione_giorni_lavorativi: user.permessi_admin?.gestione_giorni_lavorativi || false,
      gestione_limiti_richieste: user.permessi_admin?.gestione_limiti_richieste || false,
      gestione_eventi: user.permessi_admin?.gestione_eventi || false,
      gestione_ferie_permessi: user.permessi_admin?.gestione_ferie_permessi || false
    });
    setPermissionsDialogOpen(true);
  };

  const handleBalanceClick = async (user) => {
    setSelectedUser(user);
    try {
      const response = await axios.get(`${API}/users/${user.id}/leave-balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalanceForm({
        ferie_totali_annue: response.data.ferie_totali_annue || 26,
        ferie_riporto: response.data.ferie_riporto || 0,
        ferie_godute: response.data.ferie_godute || 0,
        permessi_totali_annui: response.data.permessi_totali_annui || 32,
        permessi_riporto: response.data.permessi_riporto || 0,
        permessi_goduti: response.data.permessi_goduti || 0
      });
    } catch (error) {
      setBalanceForm({
        ferie_totali_annue: 26,
        ferie_riporto: 0,
        ferie_godute: 0,
        permessi_totali_annui: 32,
        permessi_riporto: 0,
        permessi_goduti: 0
      });
    }
    setBalanceDialogOpen(true);
  };

  const handleSaveBalance = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ferie_totali_annue: balanceForm.ferie_totali_annue,
        ferie_riporto: balanceForm.ferie_riporto,
        ferie_godute: balanceForm.ferie_godute,
        permessi_totali_annui: balanceForm.permessi_totali_annui,
        permessi_riporto: balanceForm.permessi_riporto,
        permessi_goduti: balanceForm.permessi_goduti
      };
      await axios.put(`${API}/users/${selectedUser.id}/leave-balance`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bilancio ferie/permessi aggiornato!");
      setBalanceDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento del bilancio");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, {
        permessi_admin: permissionsForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Permessi aggiornati con successo!");
      setPermissionsDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento dei permessi");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utente aggiornato con successo!");
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utente eliminato con successo!");
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const dipendenti = users.filter(u => u.ruolo === "dipendente");
  const amministrativi = users.filter(u => u.ruolo === "amministrativo");
  const titolari = users.filter(u => u.ruolo === "titolare");

  // Sort alphabetically by cognome, then nome
  const sortAlphabetically = (a, b) => {
    const cognomeCompare = a.cognome.localeCompare(b.cognome, 'it');
    if (cognomeCompare !== 0) return cognomeCompare;
    return a.nome.localeCompare(b.nome, 'it');
  };

  // Combine: Titolari first, then Amministrativi, then Dipendenti - all sorted alphabetically
  const sortedUsers = [
    ...titolari.sort(sortAlphabetically),
    ...amministrativi.sort(sortAlphabetically),
    ...dipendenti.sort(sortAlphabetically)
  ];

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="users-title">
              {currentUser?.ruolo === "titolare" ? "Gestione Dipendenti" : "Team Aziendale"}
            </h1>
            <p className="text-slate-500 mt-1">
              {users.length} utenti • {titolari.length} titolari • {amministrativi.length} amministrativi • {dipendenti.length} dipendenti
            </p>
          </div>
        </div>

        {/* Istruzioni */}
        {(currentUser?.ruolo === "titolare" || (currentUser?.ruolo === "amministrativo")) && (
          <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg shadow-sm">
            <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
              <Info className="h-5 w-5" />
              Istruzioni per la modifica di ogni singolo amministrativo o dipendente
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-amber-900">
              <div className="flex items-center gap-2 bg-white/50 p-2 rounded">
                <Shield className="h-5 w-5 text-purple-600 shrink-0" />
                <span>Selezionando lo <strong>scudo</strong> autorizzi gli amministrativi ad eseguire azioni</span>
              </div>
              {settings?.mostra_prospetto_ferie !== false && (
                <div className="flex items-center gap-2 bg-white/50 p-2 rounded">
                  <Calendar className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span>Selezionando il <strong>calendario</strong> configuri il calcolo esatto delle ferie e permessi per i dipendenti</span>
                </div>
              )}
              <div className="flex items-center gap-2 bg-white/50 p-2 rounded">
                <Pencil className="h-5 w-5 text-blue-600 shrink-0" />
                <span>Selezionando la <strong>matita</strong> puoi variare/correggere nome, e-mail e modificare il ruolo dei dipendenti</span>
              </div>
              <div className="flex items-center gap-2 bg-white/50 p-2 rounded">
                <Trash2 className="h-5 w-5 text-red-600 shrink-0" />
                <span>Selezionando il <strong>cestino</strong> cancelli il dipendente in caso di licenziamento</span>
              </div>
            </div>
          </div>
        )}

        {/* Users List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Elenco Utenti</CardTitle>
            <CardDescription>
              {currentUser?.ruolo === "titolare" 
                ? "Ordinati per ruolo (Titolari, Amministrativi, Dipendenti) e in ordine alfabetico"
                : "Visualizza tutti i membri del team"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sortedUsers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <User className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Nessun utente trovato</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {sortedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-4 hover:bg-slate-50 transition-colors"
                    data-testid={`user-item-${user.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {user.nome} {user.cognome}
                          {user.posizione && (
                            <span className="ml-2 text-sm font-normal text-slate-500">({user.posizione})</span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        className={
                          user.ruolo === "titolare"
                            ? "bg-violet-100 text-violet-800 hover:bg-violet-100"
                            : user.ruolo === "amministrativo"
                            ? "bg-theme-100 text-theme-800 hover:bg-theme-100"
                            : "bg-slate-100 text-slate-800 hover:bg-slate-100"
                        }
                      >
                        {user.ruolo === "titolare" ? "Titolare" : user.ruolo === "amministrativo" ? "Amministrativo" : "Dipendente"}
                      </Badge>
                      {/* Action buttons based on role and permissions */}
                      <div className="flex items-center gap-1">
                        {/* Titolare can do everything */}
                        {currentUser?.ruolo === "titolare" && (
                          <>
                            {/* Permissions button only for amministrativo users */}
                            {user.ruolo === "amministrativo" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePermissionsClick(user)}
                                className="text-theme-500 hover:text-theme-700 hover:bg-theme-50"
                                data-testid={`permissions-user-${user.id}`}
                                title="Gestisci le azioni che possono eseguire"
                              >
                                <Shield className="h-4 w-4" />
                              </Button>
                            )}
                            {settings?.mostra_prospetto_ferie !== false && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleBalanceClick(user)}
                                className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                                data-testid={`balance-user-${user.id}`}
                                title="Gestisci ferie e permessi"
                              >
                                <Calendar className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditClick(user)}
                              className="text-slate-400 hover:text-slate-900"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteClick(user)}
                              className="text-slate-400 hover:text-red-500"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {/* Amministrativo with gestione_ferie_permessi can configure leave balances */}
                        {currentUser?.ruolo === "amministrativo" && currentUser?.permessi_admin?.gestione_ferie_permessi && settings?.mostra_prospetto_ferie !== false && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleBalanceClick(user)}
                            className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid={`balance-user-${user.id}`}
                            title="Gestisci ferie e permessi"
                          >
                            <Calendar className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance Dialog */}
        <Dialog open={balanceDialogOpen} onOpenChange={setBalanceDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Gestione Ferie e Permessi</DialogTitle>
              <DialogDescription>
                Configura ferie e permessi per {selectedUser?.nome} {selectedUser?.cognome}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4 overflow-y-auto flex-1">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800 space-y-1">
                    <p><strong>Nota importante:</strong></p>
                    <p>I dati visualizzati sono puramente indicativi e potrebbero non rispecchiare la situazione reale.</p>
                    <p>Non hanno alcun valore legale e non possono essere utilizzati per fini ufficiali.</p>
                    <p>Per i dati certificati fare sempre riferimento alla busta paga o alla documentazione fornita dall'azienda.</p>
                    <p>Da tali informazioni non deriva alcun diritto né obbligo per nessuna delle parti.</p>
                  </div>
                </div>
              </div>
              
              {/* Sezione FERIE */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <h3 className="text-lg font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  FERIE (giorni)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-emerald-700">Giorni di ferie spettanti all'anno</Label>
                    <Input
                      type="number"
                      min="0"
                      value={balanceForm.ferie_totali_annue}
                      onChange={(e) => setBalanceForm({...balanceForm, ferie_totali_annue: parseInt(e.target.value) || 0})}
                      className="text-center text-lg font-semibold"
                      placeholder="Es: 32"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-emerald-700">Riporto ferie anno precedente (se maturate)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={balanceForm.ferie_riporto}
                      onChange={(e) => setBalanceForm({...balanceForm, ferie_riporto: parseInt(e.target.value) || 0})}
                      className="text-center text-lg font-semibold"
                      placeholder="Es: 5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-emerald-700">Ferie già godute nell'anno</Label>
                    <Input
                      type="number"
                      min="0"
                      value={balanceForm.ferie_godute}
                      onChange={(e) => setBalanceForm({...balanceForm, ferie_godute: parseInt(e.target.value) || 0})}
                      className="text-center text-lg font-semibold"
                      placeholder="Es: 7"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-emerald-700 font-semibold">Risultato ferie dopo calcolo</Label>
                    <div className="h-10 flex items-center justify-center bg-emerald-100 border-2 border-emerald-400 rounded-md text-xl font-bold text-emerald-800">
                      {(balanceForm.ferie_totali_annue || 0) + (balanceForm.ferie_riporto || 0) - (balanceForm.ferie_godute || 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sezione PERMESSI */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="text-lg font-semibold text-amber-800 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  PERMESSI (ore)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-amber-700">Ore di permesso spettanti all'anno</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={balanceForm.permessi_totali_annui}
                      onChange={(e) => setBalanceForm({...balanceForm, permessi_totali_annui: parseFloat(e.target.value) || 0})}
                      className="text-center text-lg font-semibold"
                      placeholder="Es: 40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-amber-700">Riporto permessi anno precedente (se maturati)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={balanceForm.permessi_riporto}
                      onChange={(e) => setBalanceForm({...balanceForm, permessi_riporto: parseFloat(e.target.value) || 0})}
                      className="text-center text-lg font-semibold"
                      placeholder="Es: 5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-amber-700">Permessi già goduti nell'anno</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={balanceForm.permessi_goduti}
                      onChange={(e) => setBalanceForm({...balanceForm, permessi_goduti: parseFloat(e.target.value) || 0})}
                      className="text-center text-lg font-semibold"
                      placeholder="Es: 10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-amber-700 font-semibold">Risultato permessi dopo calcolo</Label>
                    <div className="h-10 flex items-center justify-center bg-amber-100 border-2 border-amber-400 rounded-md text-xl font-bold text-amber-800">
                      {((balanceForm.permessi_totali_annui || 0) + (balanceForm.permessi_riporto || 0) - (balanceForm.permessi_goduti || 0)).toFixed(1)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBalanceDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleSaveBalance} disabled={saving} className="btn-theme">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifica Utente</DialogTitle>
              <DialogDescription>
                Modifica i dati dell'utente {selectedUser?.nome} {selectedUser?.cognome}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cognome</Label>
                  <Input
                    value={editForm.cognome}
                    onChange={(e) => setEditForm({ ...editForm, cognome: e.target.value })}
                    data-testid="edit-cognome"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={editForm.nome}
                    onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                    data-testid="edit-nome"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  data-testid="edit-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select
                  value={editForm.ruolo}
                  onValueChange={(v) => setEditForm({ ...editForm, ruolo: v })}
                >
                  <SelectTrigger data-testid="edit-ruolo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dipendente">Dipendente</SelectItem>
                    <SelectItem value="amministrativo">Amministrativo</SelectItem>
                    <SelectItem value="titolare">Titolare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                className="btn-theme"
                data-testid="confirm-edit-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Salva Modifiche
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
              <AlertDialogDescription>
                Sei sicuro di voler eliminare l'utente <strong>{selectedUser?.nome} {selectedUser?.cognome}</strong>?
                <br /><br />
                Questa azione è irreversibile. Verranno eliminate anche tutte le richieste di ferie/permessi associate a questo utente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Permissions Dialog for Amministrativo */}
        <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-theme-500" />
                Permessi Impostazioni
              </DialogTitle>
              <DialogDescription>
                Gestisci i permessi di <strong>{selectedUser?.nome} {selectedUser?.cognome}</strong> per modificare le impostazioni dell'applicazione.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto flex-1">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="space-y-0.5 flex-1 mr-3">
                  <Label className="text-sm font-medium">Configura ferie e permessi</Label>
                  <p className="text-xs text-slate-500">L'amministrativo può configurare la Gestione Ferie e Permessi dei dipendenti</p>
                </div>
                <Switch
                  checked={permissionsForm.gestione_ferie_permessi}
                  onCheckedChange={(v) => setPermissionsForm({ ...permissionsForm, gestione_ferie_permessi: v })}
                  data-testid="perm-ferie-permessi"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-theme-50 rounded-lg border border-theme-100">
                <div className="space-y-0.5 flex-1 mr-3">
                  <Label className="text-sm font-medium">Aggiungi eventi</Label>
                  <p className="text-xs text-slate-500">Può aggiungere Straordinario e Malattia dei dipendenti nel calendario</p>
                </div>
                <Switch
                  checked={permissionsForm.gestione_eventi}
                  onCheckedChange={(v) => setPermissionsForm({ ...permissionsForm, gestione_eventi: v })}
                  data-testid="perm-eventi"
                />
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t pt-4">
              <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleSavePermissions}
                disabled={saving}
                className="btn-theme"
                data-testid="confirm-permissions-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Salva Permessi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Instructions Dialog */}
        <Dialog open={adminInstructionsDialogOpen} onOpenChange={handleCloseAdminInstructions}>
          <DialogContent className="sm:max-w-md" data-testid="admin-instructions-dialog">
            <DialogHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-100)' }}>
                {adminInstructionsContent.hasAdmin ? (
                  <UserCog className="h-8 w-8" style={{ color: 'var(--theme-600)' }} />
                ) : (
                  <Info className="h-8 w-8" style={{ color: 'var(--theme-600)' }} />
                )}
              </div>
              <DialogTitle className="text-xl font-semibold text-center">
                {adminInstructionsContent.hasAdmin ? "Gestione Amministrativi" : "Promemoria"}
              </DialogTitle>
              <DialogDescription className="text-center text-base text-slate-600 leading-relaxed">
                {adminInstructionsContent.message}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6 sm:justify-center">
              <Button 
                onClick={handleCloseAdminInstructions}
                style={{ backgroundColor: 'var(--theme-600)' }}
                className="hover:opacity-90 px-8"
                data-testid="close-admin-instructions-btn"
              >
                Ho capito
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
